// src/components/books/modals/MediaModal.jsx
import React, { useState } from 'react';
import { X, Upload, Link, Image, Video, File, Loader2, AlertCircle } from 'lucide-react';
import { storage } from '../../../config/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

export const MediaModal = ({ isOpen, onClose, onSave, currentValue, bookId }) => {
  const [activeTab, setActiveTab] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState(currentValue || '');
  const [previewUrl, setPreviewUrl] = useState(currentValue || '');
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  if (!isOpen) return null;

  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      // Validate file type and size
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'video/mp4', 'video/webm', 'video/ogg',
        'application/pdf', 'text/plain'
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error('File type not supported');
      }

      // Create unique filename
      const timestamp = Date.now();
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `pb-${bookId}/media/${timestamp}_${cleanFileName}`;

      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      setPreviewUrl(downloadURL);
      setUrlInput(downloadURL);
    } catch (error) {
      console.error('Upload error:', error);
      setError(error.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleUrlValidation = (url) => {
    try {
      new URL(url);
      setPreviewUrl(url);
      setError('');
      return true;
    } catch {
      setError('Please enter a valid URL');
      setPreviewUrl('');
      return false;
    }
  };

  const handleSave = () => {
    if (activeTab === 'url') {
      if (urlInput && handleUrlValidation(urlInput)) {
        onSave({ url: urlInput, type: 'url' });
      }
    } else {
      if (previewUrl) {
        onSave({ url: previewUrl, type: 'file' });
      }
    }
  };

  const getFileTypeIcon = (url) => {
    if (!url) return <File className="h-12 w-12 text-gray-400" />;
    
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('image') || lowerUrl.match(/\.(jpg|jpeg|png|gif|webp)$/)) {
      return <Image className="h-12 w-12 text-blue-500" />;
    }
    if (lowerUrl.includes('video') || lowerUrl.match(/\.(mp4|webm|ogg|avi|mov)$/)) {
      return <Video className="h-12 w-12 text-purple-500" />;
    }
    return <File className="h-12 w-12 text-gray-500" />;
  };

  const renderPreview = () => {
    if (!previewUrl) return null;

    const isImage = previewUrl.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) || 
                   previewUrl.includes('image');
    const isVideo = previewUrl.toLowerCase().match(/\.(mp4|webm|ogg)$/) || 
                   previewUrl.includes('video');

    if (isImage) {
      return (
        <div className="mt-4">
          <img 
            src={previewUrl} 
            alt="Preview" 
            className="max-w-full max-h-48 rounded-lg border"
            onError={() => setError('Failed to load image')}
          />
        </div>
      );
    }

    if (isVideo) {
      return (
        <div className="mt-4">
          <video 
            src={previewUrl} 
            controls 
            className="max-w-full max-h-48 rounded-lg border"
            onError={() => setError('Failed to load video')}
          >
            Your browser does not support video playback.
          </video>
        </div>
      );
    }

    return (
      <div className="mt-4 p-4 border rounded-lg bg-gray-50">
        <div className="flex items-center gap-3">
          {getFileTypeIcon(previewUrl)}
          <div>
            <p className="text-sm font-medium">File attached</p>
            <p className="text-xs text-gray-500">Click save to confirm</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold">Add Media</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('upload')}
            className={`px-6 py-3 font-medium border-b-2 ${
              activeTab === 'upload'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Upload className="h-4 w-4 inline mr-2" />
            Upload File
          </button>
          <button
            onClick={() => setActiveTab('url')}
            className={`px-6 py-3 font-medium border-b-2 ${
              activeTab === 'url'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Link className="h-4 w-4 inline mr-2" />
            Link URL
          </button>
        </div>

        {/* Content */}
        <div className="p-6 max-h-96 overflow-y-auto">
          {activeTab === 'upload' && (
            <div className="space-y-4">
              {/* Upload Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  dragOver 
                    ? 'border-blue-400 bg-blue-50' 
                    : 'border-gray-300 hover:border-gray-400'
                } ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
              >
                {uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500 mb-2" />
                    <p className="text-sm text-gray-600">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 mb-2">
                      Drop files here or click to upload
                    </p>
                    <p className="text-xs text-gray-400">
                      Supports: Images, Videos, PDFs (max 10MB)
                    </p>
                    
                    <input
                      type="file"
                      accept="image/*,video/*,.pdf,.txt"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) handleFileUpload(file);
                      }}
                      className="hidden"
                      id="file-upload"
                      disabled={uploading}
                    />
                    
                    <label
                      htmlFor="file-upload"
                      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>

              {/* Upload Preview */}
              {renderPreview()}
            </div>
          )}

          {activeTab === 'url' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Media URL
                </label>
                <input
                  type="url"
                  value={urlInput}
                  onChange={(e) => {
                    setUrlInput(e.target.value);
                    if (e.target.value) {
                      handleUrlValidation(e.target.value);
                    } else {
                      setPreviewUrl('');
                      setError('');
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="https://example.com/image.jpg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter a direct link to an image, video, or document
                </p>
              </div>

              {/* URL Preview */}
              {renderPreview()}

              {/* Common URL Examples */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Example URLs:</h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>• Images: https://example.com/image.jpg</p>
                  <p>• YouTube: https://youtube.com/watch?v=VIDEO_ID</p>
                  <p>• Google Drive: https://drive.google.com/file/d/FILE_ID</p>
                  <p>• Embedded content: Any public URL</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div className="text-sm text-gray-600">
            {previewUrl ? (
              <span className="text-green-600">✓ Media ready</span>
            ) : (
              'No media selected'
            )}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!previewUrl || uploading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Uploading...' : 'Save Media'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};